import { SecaoQuestionario } from "@/contexts/fiscalizacao/formulario";
import { RelatorioAMFormValues } from "@/contexts/fiscalizacao/relatorioAM/model/RelatorioAMFormValues";
import { STATUS_ALTERNATIVA_RAM } from "@/contexts/fiscalizacao/relatorioAM/model/STATUS_ALTERNATIVA_RAM";
import { UploadOutlined } from "@ant-design/icons";
import {
  Col,
  Collapse,
  Divider,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Skeleton,
  Tag,
  Typography,
  Upload,
} from "antd";
import dayjs from "dayjs";

export function QuestionarioRelatorioAM({
  secoesQuestionario,
  form,
  isLoadingQuestionario,
}: {
  secoesQuestionario: SecaoQuestionario[];
  form: FormInstance<RelatorioAMFormValues>;
  isLoadingQuestionario: boolean;
}) {
  const formulario = Form.useWatch("formulario", form);
  const questionarios = Form.useWatch("questionarios", form) || [];

  let questionarioIndex = 0;

  const getCorPeso = (peso: string) => {
    switch (peso) {
      case "Leve":
        return "green";
      case "Moderado":
        return "orange";
      case "Grave":
        return "red";
      case "Gravíssimo":
        return "purple";
      default:
        return "default";
    }
  };

  const handleAlternativeChange = (value: number, currentIndex: number) => {
    if (value) {
      // Atualiza a dataResposta para hoje
      form.setFieldValue(
        ["questionarios", currentIndex, "dataResposta"],
        dayjs().format("YYYY-MM-DD")
      );
    }

    // Se está removendo a seleção, remove também a dataResposta
    if (!value) {
      form.setFieldValue(
        ["questionarios", currentIndex, "dataResposta"],
        undefined
      );
    }
  };

  // Cria os itens para o Collapse
  const collapseItems = secoesQuestionario.map((secao, secaoIndex) => {
    // Total de perguntas na seção
    const totalPerguntas = secao.perguntas.length;

    const perguntasRespondidas = secao.perguntas.filter((_, perguntaIndex) => {
      const currentIndex = questionarioIndex + perguntaIndex;
      const resposta = questionarios[currentIndex];
      return resposta && resposta.alternativa && resposta.alternativa.id;
    });

    // Total de perguntas respondidas (com alternativa selecionada)
    const totalRespondidas = perguntasRespondidas.length;

    return {
      key: secaoIndex.toString(),
      label: secao.secao,
      forceRender: true,
      extra: (
        <Typography.Text
          type="secondary"
          style={{
            color: totalRespondidas === totalPerguntas ? "#2ECC40" : "",
          }}
        >
          {totalRespondidas}/{totalPerguntas} Respondidas
        </Typography.Text>
      ),
      children: (
        <Row gutter={[16, 16]}>
          <Col span={24} style={{ maxHeight: 1000, overflowY: "auto" }}>
            {secao.perguntas
              .filter((pergunta) => pergunta.visivel && pergunta.ativo)
              .map((pergunta, index) => {
                const currentIndex = questionarioIndex++;
                const respostaAtual = questionarios[currentIndex];

                return (
                  <div
                    key={index}
                    style={{
                      marginBottom: 24,
                      padding: 16,
                      border: "1px solid #f0f0f0",
                      borderRadius: 6,
                    }}
                  >
                    {/* Cabeçalho da pergunta */}
                    <div style={{ marginBottom: 12 }}>
                      <Typography.Title level={5} style={{ margin: 0 }}>
                        {index + 1}. {pergunta.nome}
                      </Typography.Title>

                      <div
                        style={{
                          marginTop: 8,
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <Tag color={getCorPeso(pergunta.peso)}>
                          Peso: {pergunta.peso}
                        </Tag>
                        <Tag color="blue">
                          Prazo: {pergunta.dias}{" "}
                          {pergunta.dias === 1 ? "dia" : "dias"}
                        </Tag>
                      </div>
                    </div>

                    {/* Dica da pergunta */}
                    {pergunta.dica && (
                      <Typography.Paragraph
                        type="secondary"
                        italic
                        style={{
                          backgroundColor: "#f6f6f6",
                          padding: 8,
                          borderRadius: 4,
                          marginBottom: 16,
                        }}
                      >
                        💡 <strong>Dica:</strong> {pergunta.dica}
                      </Typography.Paragraph>
                    )}

                    {/* Data da resposta - exibida apenas se existe alternativa selecionada e dataResposta */}
                    {respostaAtual?.alternativa?.id &&
                      respostaAtual?.dataResposta && (
                        <Row style={{ marginBottom: 12 }}>
                          <Col xs={24}>
                            <Typography.Text type="secondary">
                              Data Resposta:{" "}
                              {dayjs(respostaAtual.dataResposta).format(
                                "DD/MM/YYYY"
                              )}
                            </Typography.Text>
                          </Col>
                        </Row>
                      )}

                    {/* Campo hidden para o ID da pergunta */}
                    <Form.Item
                      name={["questionarios", currentIndex, "pergunta", "id"]}
                      hidden
                      initialValue={pergunta.id}
                    >
                      <Input />
                    </Form.Item>

                    {/* Campo hidden para a data da resposta */}
                    <Form.Item
                      name={["questionarios", currentIndex, "dataResposta"]}
                      hidden
                    >
                      <Input />
                    </Form.Item>

                    {/* Alternativas */}
                    <Form.Item
                      label="Selecione uma alternativa"
                      rules={[
                        {
                          required: true,
                          message: "Por favor, selecione uma alternativa.",
                        },
                      ]}
                      name={[
                        "questionarios",
                        currentIndex,
                        "alternativa",
                        "id",
                      ]}
                    >
                      <Select
                        placeholder="Selecione uma alternativa"
                        onChange={(value) =>
                          handleAlternativeChange(value, currentIndex)
                        }
                        allowClear
                        options={pergunta.alternativas
                          .filter((alt) => alt.ativo)
                          .sort((a, b) =>
                            (a.nome ?? "").localeCompare(b.nome ?? "")
                          )
                          .map((alt) => ({
                            label: (
                              <span>
                                {alt.nome}
                                <Tag
                                  style={{ marginLeft: 8 }}
                                  color={
                                    alt.tipo === "CONFORMIDADE"
                                      ? "green"
                                      : alt.tipo === "INCONFORMIDADE"
                                        ? "red"
                                        : "default"
                                  }
                                >
                                  {alt.tipo && STATUS_ALTERNATIVA_RAM[alt.tipo]}
                                </Tag>
                              </span>
                            ),
                            value: alt.id,
                          }))}
                      />
                    </Form.Item>

                    {/* Observação */}
                    <Form.Item
                      label="Observação (opcional)"
                      name={["questionarios", currentIndex, "observacao"]}
                    >
                      <Input.TextArea
                        placeholder="Adicione uma observação"
                        rows={3}
                      />
                    </Form.Item>

                    {/* Upload de anexo */}
                    <Form.Item
                      label="Anexos (opcional)"
                      name={["questionarios", currentIndex, "anexo"]}
                      valuePropName="fileList"
                      getValueFromEvent={(e) =>
                        Array.isArray(e) ? e : e?.fileList
                      }
                    >
                      <Upload
                        name="anexo"
                        listType="text"
                        multiple
                        beforeUpload={() => false}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      >
                        <Typography.Link>
                          <UploadOutlined /> Clique para enviar anexos
                        </Typography.Link>
                      </Upload>
                    </Form.Item>
                  </div>
                );
              })}
          </Col>
        </Row>
      ),
    };
  });

  if (isLoadingQuestionario) {
    return (
      <>
        <Divider orientation="left" plain>
          <Typography.Title level={5}>Questionários</Typography.Title>
        </Divider>
        <Skeleton active paragraph={{ rows: 4 }} />
      </>
    );
  }

  if (!secoesQuestionario.length) {
    return (
      <>
        <Divider orientation="left" plain>
          <Typography.Title level={5}>Questionários</Typography.Title>
        </Divider>
        <Typography.Text type="secondary">
          {formulario?.id
            ? "O formulário selecionado não possui perguntas cadastradas."
            : "Selecione um formulário para carregar as perguntas do questionário."}
        </Typography.Text>
      </>
    );
  }

  return (
    <>
      <Divider orientation="left" plain>
        <Typography.Title level={5}>Questionários</Typography.Title>
      </Divider>

      <Row gutter={[16, 16]} justify="center">
        <Col xs={24}>
          <Collapse items={collapseItems} accordion />
        </Col>
      </Row>
    </>
  );
}
